/**
 * @module Helper Methods
 */
import * as addresses from "./resources/addresses";
import * as keys from "./resources/keys";
import * as postcodes from "./resources/postcodes";
import * as udprn from "./resources/udprn";
import * as umprn from "./resources/umprn";
import { appendAuthorization, appendPage, appendIp, appendFilter, appendTags } from "./util";
import { IdpcPostcodeNotFoundError, IdpcUmprnNotFoundError, IdpcUdprnNotFoundError } from "./error";
/**
 * Ping API base (`/`)
 *
 * Dispatches HTTP request to root endpoint "`/`"
 */

export var ping = function ping(client) {
  var method = "GET";
  var url = "".concat(client.protocol(), "://").concat(client.config.baseUrl, "/");
  return client.config.agent.http({
    method: method,
    url: url,
    header: {},
    query: {},
    timeout: client.config.timeout
  });
};
/**
 * Lookup Postcode
 *
 * Search for addresses given a postcode. Postcode queries are case and space insensitive
 *
 * Invalid postcodes return an empty array address result `[]`
 *
 * [API Documentation for /postcodes](https://ideal-postcodes.co.uk/documentation/postcodes#postcode)
 */

export var lookupPostcode = function lookupPostcode(options) {
  var queryOptions = toAddressIdQuery(options);
  var page = options.page;
  if (page !== undefined) queryOptions.query.page = page.toString();
  return postcodes.retrieve(options.client, options.postcode, queryOptions).then(function (response) {
    return response.body.result;
  }).catch(function (error) {
    if (error instanceof IdpcPostcodeNotFoundError) return [];
    throw error;
  });
};
/**
 * Lookup Address
 *
 * Search for an address given a query
 *
 * [API Documentation for /addresses](https://ideal-postcodes.co.uk/documentation/addresses#query)
 */

export var lookupAddress = function lookupAddress(options) {
  var header = {};
  var query = {
    query: options.query
  };
  var client = options.client;
  appendAuthorization({
    client: client,
    header: header,
    options: options
  });
  appendIp({
    header: header,
    options: options
  });
  appendFilter({
    query: query,
    options: options
  });
  appendTags({
    client: client,
    query: query,
    options: options
  });
  appendPage({
    query: query,
    options: options
  });
  var queryOptions = {
    header: header,
    query: query
  };
  if (options.timeout !== undefined) queryOptions.timeout = options.timeout;
  return addresses.list(client, queryOptions).then(function (response) {
    return response.body.result.hits;
  });
};
/**
 * Generates a request object. Bundles together commonly used header/query extractions:
 * - Authorization (api_key, licensee, user_token)
 * - Source IP forwarding
 * - Result filtering
 * - Tagging
 */

var toAddressIdQuery = function toAddressIdQuery(options) {
  var header = {};
  var query = {};
  var client = options.client;
  appendAuthorization({
    client: client,
    header: header,
    options: options
  });
  appendIp({
    header: header,
    options: options
  });
  appendFilter({
    query: query,
    options: options
  });
  appendTags({
    client: client,
    query: query,
    options: options
  });
  var request = {
    header: header,
    query: query
  };
  if (options.timeout !== undefined) request.timeout = options.timeout;
  return request;
};
/**
 * Lookup UDPRN
 *
 * Search for an address given a UDPRN
 *
 * Invalid UDPRN returns `null`
 *
 * [API Documentation for /udprn](https://ideal-postcodes.co.uk/documentation/udprn)
 */


export var lookupUdprn = function lookupUdprn(options) {
  var queryOptions = toAddressIdQuery(options);
  return udprn.retrieve(options.client, options.udprn.toString(), queryOptions).then(function (response) {
    return response.body.result;
  }).catch(function (error) {
    if (error instanceof IdpcUdprnNotFoundError) return null;
    throw error;
  });
};
/**
 * Lookup UMPRN
 *
 * Search for an address given a UDPRN
 *
 * Invalid UDPRN returns `null`
 *
 * [API Documentation for /udprn](https://ideal-postcodes.co.uk/documentation/udprn)
 */

export var lookupUmprn = function lookupUmprn(options) {
  var queryOptions = toAddressIdQuery(options);
  return umprn.retrieve(options.client, options.umprn.toString(), queryOptions).then(function (response) {
    return response.body.result;
  }).catch(function (error) {
    if (error instanceof IdpcUmprnNotFoundError) return null;
    throw error;
  });
};
/**
 * Check Key Availability
 *
 * Checks if a key can bey used
 *
 * [API Documentation for /keys]()https://ideal-postcodes.co.uk/documentation/keys#key)
 */

export var checkKeyUsability = function checkKeyUsability(options) {
  var client = options.client,
      timeout = options.timeout;
  var api_key = options.api_key || options.client.config.api_key;
  var licensee = options.licensee;
  var query;

  if (licensee === undefined) {
    query = {};
  } else {
    query = {
      licensee: licensee
    };
  }

  var queryOptions = {
    query: query,
    header: {}
  };
  if (timeout !== undefined) queryOptions.timeout = timeout;
  return keys.retrieve(client, api_key, queryOptions).then(function (response) {
    return response.body.result;
  }); // Assert that we're retrieving public key information as no user_token provided
};